<template>
  <div class="page-stack">
    <div class="sticky-toolbar">
      <a-card :bordered="false" class="sticky-toolbar-card">
        <a-space direction="vertical" style="width:100%" size="middle">
          <a-segmented v-model:value="mode" :options="modeOptions" />

          <a-space wrap align="center" v-if="mode === 'CONTRACTOR'">
            <div class="toolbar-label">Заказчик:</div>
            <a-select
              v-model:value="contractorId"
              :options="contractorOptions"
              style="min-width:320px"
              placeholder="Выберите заказчика"
              show-search
              :filter-option="filterContractors"
            />

            <div class="toolbar-label">С:</div>
            <a-date-picker v-model:value="dateFrom" format="YYYY-MM-DD" allow-clear inputReadOnly />

            <div class="toolbar-label">По:</div>
            <a-date-picker v-model:value="dateTo" format="YYYY-MM-DD" allow-clear inputReadOnly />

            <a-space>
              <a-button size="small" @click="applyRangePreset('today')">Сегодня</a-button>
              <a-button size="small" @click="applyRangePreset('week')">7 дней</a-button>
              <a-button size="small" @click="applyRangePreset('month')">30 дней</a-button>
              <a-button size="small" @click="applyRangePreset('clear')">Сбросить</a-button>
            </a-space>

            <a-button type="primary" @click="loadContractorAudit" :loading="loading" :disabled="!contractorId">
              Показать
            </a-button>
          </a-space>

          <a-space wrap align="center" v-else>
            <div class="toolbar-label">Заказчик:</div>
            <a-select
              v-model:value="orderContractorId"
              :options="contractorOptions"
              style="min-width:320px"
              placeholder="Выберите заказчика"
              show-search
              :filter-option="filterContractors"
            />

            <div class="toolbar-label">Дата заказа:</div>
            <a-date-picker v-model:value="orderDate" format="YYYY-MM-DD" :allowClear="false" inputReadOnly />
          </a-space>
        </a-space>
      </a-card>
    </div>

    <a-alert
      v-if="headerText"
      type="info"
      show-icon
      :message="headerText"
    />

    <a-card title="Аудит" :bordered="false">
      <a-empty v-if="!items.length && !loading" description="Нет записей аудита" />

      <a-table
        v-else
        :dataSource="items"
        :columns="columns"
        :loading="loading"
        :pagination="false"
        rowKey="audit_id"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'created_at'">
            {{ formatDateTime(record.created_at) }}
          </template>

          <template v-else-if="column.key === 'order'">
            <div>{{ record.order_date || '—' }}</div>
            <div class="cell-muted">Заказ #{{ record.order_id }}</div>
          </template>

          <template v-else-if="column.key === 'user'">
            <div>{{ record.user?.name || 'Система' }}</div>
            <div class="cell-muted">{{ label(record.user?.role || '—') }}</div>
          </template>

          <template v-else-if="column.key === 'action'">
            {{ actionLabel(record.action) }}
          </template>

          <template v-else-if="column.key === 'details'">
            {{ actionDetails(record) }}
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { getKitchenContractors } from '../../api/contractors'
import { getKitchenContractorAudit, getKitchenOrderAudit } from '../../api/audit'
import { getKitchenOrderByContractorDate } from '../../api/kitchenOrders'
import { label } from '../../utils/labels'

const mode = ref('CONTRACTOR')
const loading = ref(false)

const contractors = ref([])
const contractorId = ref(null)
const orderContractorId = ref(null)
const orderDate = ref(dayjs())
const dateFrom = ref(null)
const dateTo = ref(null)

const items = ref([])
const currentContractor = ref(null)
const currentOrder = ref(null)
const currentFilter = ref({ date_from: null, date_to: null })

const modeOptions = [
  { value: 'CONTRACTOR', label: 'По заказчику' },
  { value: 'ORDER', label: 'По заказу' },
]

const columns = [
  { title: 'Когда', key: 'created_at', width: 180 },
  { title: 'Заказ', key: 'order', width: 150 },
  { title: 'Кто', key: 'user', width: 180 },
  { title: 'Действие', key: 'action', width: 220 },
  { title: 'Подробности', key: 'details' },
]

const contractorOptions = computed(() =>
  contractors.value
    .filter(item => item.active)
    .map(item => ({ value: item.id, label: item.name }))
)

const headerText = computed(() => {
  if (mode.value === 'ORDER' && currentOrder.value) {
    const contractorName = contractors.value.find(item => item.id === currentOrder.value.contractor_id)?.name
    return contractorName
      ? `Аудит заказа ${contractorName} на ${currentOrder.value.date}`
      : `Аудит заказа на ${currentOrder.value.date}`
  }

  if (mode.value === 'CONTRACTOR' && currentContractor.value) {
    const from = currentFilter.value.date_from || 'начала'
    const to = currentFilter.value.date_to || 'сейчас'
    return `Аудит заказчика ${currentContractor.value.name}, период: ${from} - ${to}`
  }

  return ''
})

function filterContractors(input, option) {
  return String(option?.label || '').toLowerCase().includes(String(input).toLowerCase())
}

function formatDateTime(value) {
  if (!value) return '—'
  return dayjs(value).format('YYYY-MM-DD HH:mm')
}

function applyRangePreset(preset) {
  const today = dayjs()

  if (preset === 'today') {
    dateFrom.value = today
    dateTo.value = today
    return
  }

  if (preset === 'week') {
    dateFrom.value = today.subtract(6, 'day')
    dateTo.value = today
    return
  }

  if (preset === 'month') {
    dateFrom.value = today.subtract(29, 'day')
    dateTo.value = today
    return
  }

  dateFrom.value = null
  dateTo.value = null
}

function actionLabel(action) {
  const map = {
    UPDATE_MEAL_QTY: 'Изменено количество порций',
    UPDATE_SPECIAL_QTY: 'Изменено количество спецпитания',
    UPDATE_COMMENT: 'Изменен комментарий',
    ADD_CHILD_MEAL: 'Ребенок добавлен в прием пищи',
    REMOVE_CHILD_MEAL: 'Ребенок удален из приема пищи',
  }

  return map[action] || action || '—'
}

function actionDetails(record) {
  const payload = record?.payload || {}

  switch (record?.action) {
    case 'UPDATE_MEAL_QTY':
      return `${label(payload.meal)}: ${payload.from ?? 0} -> ${payload.to ?? 0}`
    case 'UPDATE_SPECIAL_QTY':
      return `${label(payload.meal)}: special #${payload.special_type_id ?? '—'} -> ${payload.to ?? 0}`
    case 'UPDATE_COMMENT':
      return payload.to ? `Новый комментарий: ${payload.to}` : 'Комментарий очищен'
    case 'ADD_CHILD_MEAL':
      return `Ребенок #${payload.child_id ?? '—'} добавлен в ${label(payload.meal)}${payload.note ? `, note: ${payload.note}` : ''}`
    case 'REMOVE_CHILD_MEAL':
      return `Ребенок #${payload.child_id ?? '—'} удален из ${label(payload.meal)}`
    default:
      return payload && Object.keys(payload).length ? JSON.stringify(payload) : '—'
  }
}

async function loadContractors() {
  try {
    const res = await getKitchenContractors()
    contractors.value = res.data.data || []
    if (!contractorId.value) {
      contractorId.value = contractors.value.find(item => item.active)?.id ?? null
    }
    if (!orderContractorId.value) {
      orderContractorId.value = contractors.value.find(item => item.active)?.id ?? null
    }
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить заказчиков')
  }
}

async function loadContractorAudit() {
  if (!contractorId.value) return

  loading.value = true
  try {
    const params = {}
    if (dateFrom.value) params.date_from = dayjs(dateFrom.value).format('YYYY-MM-DD')
    if (dateTo.value) params.date_to = dayjs(dateTo.value).format('YYYY-MM-DD')

    const res = await getKitchenContractorAudit(contractorId.value, params)
    const data = res.data?.data || {}

    items.value = data.items || []
    currentContractor.value = data.contractor || contractors.value.find(item => item.id === contractorId.value) || null
    currentFilter.value = data.filter || { date_from: null, date_to: null }
    currentOrder.value = null
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить аудит')
  } finally {
    loading.value = false
  }
}

async function loadOrderAudit() {
  if (!orderContractorId.value || !orderDate.value) return

  loading.value = true
  try {
    const orderRes = await getKitchenOrderByContractorDate(
      orderContractorId.value,
      dayjs(orderDate.value).format('YYYY-MM-DD')
    )

    const foundOrderId = orderRes.data?.data?.order?.id
    if (!foundOrderId) {
      items.value = []
      currentOrder.value = null
      currentContractor.value = null
      currentFilter.value = { date_from: null, date_to: null }
      message.warning('Заказ на выбранную дату не найден')
      return
    }

    const res = await getKitchenOrderAudit(foundOrderId)
    const data = res.data?.data || {}

    items.value = data.items || []
    currentOrder.value = data.order || null
    currentContractor.value = null
    currentFilter.value = { date_from: null, date_to: null }
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить аудит заказа')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadContractors()
  if (contractorId.value) {
    await loadContractorAudit()
  }
})

watch([orderContractorId, orderDate, mode], async ([nextContractorId, nextOrderDate, nextMode]) => {
  if (nextMode !== 'ORDER') return
  if (!nextContractorId || !nextOrderDate) return
  await loadOrderAudit()
})
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.toolbar-label {
  font-weight: 600;
}

.cell-muted {
  color: #777;
  font-size: 12px;
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }
}
</style>
